// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type PointTooltipProps } from '@nivo/line';
import { numberToFixed, classNames, formatDate } from '@shared/ui-kit/utils';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const PerformanceLineChartTooltip: FC<PointTooltipProps> = ({
  point: {
    serieId,
    data: { yFormatted, xFormatted, ...extraData },
  },
}) => {
  const variation = useMemo(
    /* istanbul ignore next */
    () => (extraData as { variation?: number })?.variation,
    [extraData],
  );

  const { i18n } = useTranslation();

  const date = useMemo(
    () => formatDate(String(xFormatted), 'medium', i18n.language) ?? '',
    [i18n.language, xFormatted],
  );

  return (
    <div className="flex flex-col rounded-md bg-white p-1.5 text-center font-poppins text-sm text-secondary-dark shadow-lg">
      <span className="font-semibold">{serieId}</span>
      <span>
        {yFormatted}&nbsp;&nbsp;
        {variation && (
          <span
            className={classNames({
              'text-primary-green': variation > 0,
              'text-primary-red': variation <= 0,
            })}
          >
            {variation > 0 && '+'}
            {numberToFixed(variation)}%
          </span>
        )}
      </span>
      <span>{date}</span>
    </div>
  );
};
